import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { useChargePointsStore } from '../../store/charge-points-store';
import { useLocationStore } from '@/features/location/store/location-store';
import { useChargePointActions } from '../../hooks/useChargePointActions';
import {
  buildNearbyRows,
  formatDistance,
  formatPriceLabel,
  filledBlocks,
  type NearbyRow,
} from '../../utils/nearby';

const MAX_CARDS = 10;
const MAX_BLOCKS = 20;

const STATUS_LABEL: Record<NearbyRow['status'], string> = {
  available: 'common.status.available',
  busy: 'common.status.busy',
  offline: 'common.status.disconnected',
};

interface NearbyCarouselProps {
  /** Opens the full details sheet for the selected charger */
  onOpenDetails: () => void;
}

/**
 * Mobile: swipeable cards for the nearest chargers. Swiping to a card selects
 * it (pin highlights, map keeps it in view); tapping the selected card opens
 * the details sheet. The order is frozen while something is selected, so
 * map pans don't re-sort the cards under the user's finger.
 */
export function NearbyCarousel({ onOpenDetails }: NearbyCarouselProps) {
  const { t, i18n } = useTranslation();
  const chargePoints = useChargePointsStore((s) => s.singleChargePoints);
  const clusterCount = useChargePointsStore(
    (s) => s.clusterChargePoints.length
  );
  const selected = useChargePointsStore((s) => s.selectedChargePoint);
  const selectChargePoint = useChargePointsStore((s) => s.selectChargePoint);
  const closeDetails = useChargePointsStore((s) => s.closeDetails);
  const center = useLocationStore((s) => s.mapCenter);
  const bounds = useLocationStore((s) => s.visibleRegion);
  const { startCharging, openDirections } = useChargePointActions();

  const live = useMemo(
    () => buildNearbyRows(chargePoints, center, bounds, MAX_CARDS),
    [chargePoints, center, bounds]
  );

  // Freeze the order while a charger is selected; make sure it's included
  const frozen = useRef<NearbyRow[]>(live);
  if (!selected) frozen.current = live;
  let rows = frozen.current;
  if (selected && !rows.some((r) => r.wrapper.id === selected.id)) {
    const own = buildNearbyRows([selected], center, null, 1);
    rows = [...own, ...rows].slice(0, MAX_CARDS);
    frozen.current = rows;
  }

  const scroller = useRef<HTMLDivElement>(null);
  // Set only by real swipe movement - taps and programmatic scrolls don't
  const swiped = useRef(false);
  const settleTimer = useRef<number>();

  // Bring the selected card into view (pin tap, deep link, card tap)
  useEffect(() => {
    if (!selected) return;
    scroller.current
      ?.querySelector<HTMLElement>(`[data-card-id="${selected.id}"]`)
      ?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // After a user swipe settles, select the card closest to the centre
  const handleScroll = () => {
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => {
      const el = scroller.current;
      if (!el || !swiped.current) return;
      swiped.current = false;
      const middle = el.scrollLeft + el.clientWidth / 2;
      const cards = [...el.querySelectorAll<HTMLElement>('[data-card-id]')];
      const nearest = cards.reduce((best, card) =>
        Math.abs(card.offsetLeft + card.offsetWidth / 2 - middle) <
        Math.abs(best.offsetLeft + best.offsetWidth / 2 - middle)
          ? card
          : best
      );
      const row = rows.find((r) => r.wrapper.id === nearest.dataset.cardId);
      if (row && row.wrapper.id !== selected?.id)
        selectChargePoint(row.wrapper);
    }, 140);
  };

  if (rows.length === 0) {
    return (
      <div className="surface-float mx-4 rounded-full px-3 py-1 text-center text-xs text-muted">
        {clusterCount > 0 ? t('nearby.zoomIn') : t('map.tapMarkersHint')}
      </div>
    );
  }

  return (
    <div
      ref={scroller}
      onScroll={handleScroll}
      onTouchMove={() => (swiped.current = true)}
      onPointerMove={(e) => {
        if (e.buttons) swiped.current = true;
      }}
      onWheel={() => (swiped.current = true)}
      className="no-scrollbar flex snap-x snap-mandatory gap-2.5 overflow-x-auto px-4 pb-1 pt-2"
    >
      {rows.map((row) => {
        const isSelected = row.wrapper.id === selected?.id;
        const price = formatPriceLabel(row.priceLabel, i18n.language);
        const isSite = row.wrapper.type === 'chargePointGroup';
        const cp = row.wrapper.chargePoint;
        const canStart = !isSite && cp && row.status === 'available';
        const blocks = Math.min(row.total, MAX_BLOCKS);
        const filled = filledBlocks(row.available, row.total, MAX_BLOCKS);

        return (
          <article
            key={row.wrapper.id}
            data-card-id={row.wrapper.id}
            onClick={() =>
              isSelected ? onOpenDetails() : selectChargePoint(row.wrapper)
            }
            className={cn(
              'surface-float w-[84vw] max-w-sm shrink-0 snap-center rounded-[26px] p-4 transition-shadow',
              isSelected && '!ring-2 !ring-monta-blue/60'
            )}
          >
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-lg font-semibold tracking-tight">
                  {row.name}
                </h3>
                <p className="truncate text-[13px] text-muted">
                  {[
                    row.maxKw && `${row.maxKw} kW`,
                    row.plug,
                    formatDistance(row.meters, i18n.language),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              {isSelected && (
                <button
                  type="button"
                  aria-label={t('common.close')}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeDetails();
                  }}
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-ink/5 text-muted"
                >
                  <svg
                    className="h-3.5 w-3.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path strokeLinecap="round" d="M6 6l12 12M18 6 6 18" />
                  </svg>
                </button>
              )}
            </div>

            <div className="mt-3 flex items-end justify-between">
              {isSite ? (
                <p className="flex items-baseline gap-1.5">
                  <span
                    className={cn(
                      'text-4xl font-semibold leading-none tracking-[-0.05em] tabular-nums',
                      row.available > 0 ? 'text-ok' : 'text-muted'
                    )}
                  >
                    {row.available}
                  </span>
                  <span className="text-sm text-muted">
                    {t('chargePoint.freeNow', { total: row.total })}
                  </span>
                </p>
              ) : (
                <p className="flex items-center gap-2 text-sm font-medium">
                  <span
                    className={cn(
                      'h-2 w-2 rounded-full',
                      row.status === 'available'
                        ? 'bg-ok'
                        : row.status === 'busy'
                          ? 'bg-warn'
                          : 'bg-muted'
                    )}
                  />
                  {t(STATUS_LABEL[row.status])}
                </p>
              )}
              <p className="text-right text-lg font-semibold tabular-nums">
                {price?.free
                  ? t('nearby.free')
                  : price
                    ? price.value
                    : t('nearby.priceVaries')}
                {price && !price.free && (
                  <span className="block text-xs font-medium text-muted">
                    {price.unit}
                  </span>
                )}
              </p>
            </div>

            {isSite && (
              <div className="mt-3 flex gap-[3px]" aria-hidden>
                {Array.from({ length: blocks }, (_, i) => (
                  <span
                    key={i}
                    className={cn(
                      'h-1.5 flex-1 rounded-full',
                      i < filled ? 'bg-ok' : 'bg-ink/10'
                    )}
                  />
                ))}
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (canStart) startCharging(cp);
                  else if (isSelected) onOpenDetails();
                  else selectChargePoint(row.wrapper);
                }}
                className="flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-monta-blue font-semibold text-white transition-colors hover:bg-monta-blue-700"
              >
                {canStart
                  ? t('chargePoint.startCharging')
                  : t('chargePoint.viewDetails')}
              </button>
              <button
                type="button"
                aria-label={t('chargePoint.getDirections')}
                onClick={(e) => {
                  e.stopPropagation();
                  openDirections(row.wrapper.coordinate);
                }}
                className="grid h-12 w-12 place-items-center rounded-2xl bg-ink/5 text-ink ring-1 ring-inset ring-ink/10"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path strokeLinejoin="round" d="m3 11 18-8-8 18-2-8z" />
                </svg>
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}
